import type { TFunction } from 'i18next'
import type {
  ExtractionProgressStep,
  ExtractionProgressStepStatus,
} from '@/components/extraction-progress-checklist'
import {
  type TypeCStandardCheckLlmTrackState,
  type TypeCStandardCheckStepId,
  type TypeCStandardCheckTaskState,
  type TypeCStandardCheckTaskStatus,
} from './typeCStandardCheckTaskState'

const TYPE_C_STANDARD_CHECK_STEP_I18N_KEY: Record<
  TypeCStandardCheckStepId,
  string
> = {
  get_applicability_context: 'getApplicabilityContext',
  discover_integrated_components: 'discoverIntegrated',
  retrieve_type_c_standards: 'retrieveTypeC',
  llm_applicability: 'llmApplicabilityTypeC',
  persist_applicability: 'persistTypeC',
}

const TYPE_C_STANDARD_CHECK_ORDERED_STEPS: TypeCStandardCheckStepId[] = [
  'get_applicability_context',
  'discover_integrated_components',
  'retrieve_type_c_standards',
  'llm_applicability',
  'persist_applicability',
]

function mapTypeCStandardCheckToExtraction(
  status: TypeCStandardCheckTaskStatus
): ExtractionProgressStepStatus {
  if (status === 'pending') {
    return 'pending'
  }
  if (status === 'active') {
    return 'active'
  }
  if (status === 'done') {
    return 'done'
  }
  return 'pending'
}

function typeCStandardCheckTrackSubtitle(
  track: TypeCStandardCheckLlmTrackState,
  translate: TFunction<'machineryDirectiveCompliance', undefined>,
  candidateCount: number | null,
  llmStep: TypeCStandardCheckTaskStatus
): string {
  if (
    track.phase === 'idle' &&
    llmStep === 'done' &&
    candidateCount !== null &&
    candidateCount === 0
  ) {
    return translate(
      'onboarding.typeCStandardCheckDiscovery.llm.skippedNoCandidates'
    )
  }
  if (track.phase === 'running') {
    return translate(
      'onboarding.typeCStandardCheckDiscovery.llm.batchProgress',
      {
        current: track.batchIndex,
        total: track.batchTotal,
      }
    )
  }
  if (track.phase === 'done') {
    return translate('onboarding.typeCStandardCheckDiscovery.llm.done')
  }
  return translate('onboarding.typeCStandardCheckDiscovery.llm.waiting')
}

function typeCStandardCheckTrackToExtractionStatus(
  track: TypeCStandardCheckLlmTrackState,
  llmStep: TypeCStandardCheckTaskStatus,
  candidateCount: number | null
): ExtractionProgressStepStatus {
  if (track.phase === 'done') {
    return 'done'
  }
  if (track.phase === 'running') {
    return 'active'
  }
  if (llmStep === 'done') {
    if (
      track.phase === 'idle' &&
      candidateCount !== null &&
      candidateCount === 0
    ) {
      return 'pending'
    }
    return 'done'
  }
  if (llmStep === 'active') {
    return 'waiting'
  }
  return 'pending'
}

export function buildTypeCStandardCheckExtractionSteps(
  state: TypeCStandardCheckTaskState,
  translate: TFunction<'machineryDirectiveCompliance', undefined>
): ExtractionProgressStep[] {
  const steps: ExtractionProgressStep[] = []
  const llmStepStatus = state.steps.llm_applicability

  for (const stepId of TYPE_C_STANDARD_CHECK_ORDERED_STEPS) {
    if (
      stepId === 'discover_integrated_components' &&
      state.steps[stepId] === 'skipped'
    ) {
      continue
    }

    if (stepId === 'llm_applicability') {
      continue
    }

    if (stepId === 'retrieve_type_c_standards') {
      const searchStepStatus = state.steps.retrieve_type_c_standards
      const candidateCount = state.retrieveTypeCCandidateCount
      let subtitle: string | undefined
      if (searchStepStatus === 'done' && candidateCount !== null) {
        subtitle = translate(
          'onboarding.typeCStandardCheckDiscovery.foundCandidates',
          { count: candidateCount }
        )
      }

      steps.push({
        id: stepId,
        title: translate(
          `onboarding.typeCStandardCheckDiscovery.steps.${TYPE_C_STANDARD_CHECK_STEP_I18N_KEY[stepId]}`
        ),
        subtitle,
        status: mapTypeCStandardCheckToExtraction(searchStepStatus),
        children: [
          {
            id: `${stepId}-analyze-applicability`,
            title: translate(
              'onboarding.typeCStandardCheckDiscovery.analyzingApplicabilityTypeC'
            ),
            subtitle: typeCStandardCheckTrackSubtitle(
              state.llmTypeC,
              translate,
              candidateCount,
              llmStepStatus
            ),
            status: typeCStandardCheckTrackToExtractionStatus(
              state.llmTypeC,
              llmStepStatus,
              candidateCount
            ),
          },
        ],
      })
      continue
    }

    steps.push({
      id: stepId,
      title: translate(
        `onboarding.typeCStandardCheckDiscovery.steps.${TYPE_C_STANDARD_CHECK_STEP_I18N_KEY[stepId]}`
      ),
      status: mapTypeCStandardCheckToExtraction(state.steps[stepId]),
    })
  }

  return steps
}
